import { createContext, useContext } from 'react';
import invariant from 'tiny-invariant';

import { isServer } from '../../helpers/environment';

interface Location extends Readonly<URL> {}

type LocationContext = string | null;

const defaultValue: LocationContext = null;
const context = createContext<LocationContext>(defaultValue);

/**
 * Provides a way to isomorphically access the current URL. It's worth noting
 * that there's some overlap here with **react-router**'s `useLocation`. The
 * notable differences are:
 *
 * - React Router's implementation does not provide access to `hostname`, `protocol` etc.
 * - This hook does not currently automatically update when the URL changes on the client
 */
const useLocation = (): Location => {
  const serverURL = useContext(context);

  const url = isServer() ? serverURL : window.location.href;

  invariant(
    url,
    'useLocation: missing URL, did you forget to include the Location provider on the server?',
  );

  try {
    return new URL(url);
  } catch (error) {
    throw new Error(
      'useLocation: unable to parse URL, did you pass a valid URL to the Location provider on the server?',
    );
  }
};

const { Provider } = context;

export { LocationContext, Provider, defaultValue, useLocation };
